import * as coreClient from "@azure/core-client";

export interface FareSelectorRequest {
  /** Customer Type Code */
  customerType?: string;
  /** Zone Identifier */
  zone?: string;
}

export interface FareSelectorList {
  customerTypes?: CustomerType[];
  zones?: TravelArea[];
  travelTimes?: TravelTime[];
  travelMethods?: TravelMethod[];
}

export interface CustomerType {
  /** Customer Type Code */
  typeCode?: string;
  /** Customer Type Name */
  description?: string;
  lowerAgeBand?: number;
  upperAgeBand?: number;
  ageDescription?: string;
}

export interface TravelArea {
  /** Zone Identifier */
  id?: string;
  travelArea?: string;
}

export interface TravelTime {
  /** Travel Time Identifier */
  id?: string;
  /** Travel Time Name */
  name?: string;
  /** Travel Time Enabled / Disabled Flag for View */
  enabled?: boolean;
}

export interface TravelMethod {
  id?: string;
  /** Travel Method Name */
  travelMethodName?: string;
  /** Travel Method Enabled / Disabled Flag for View */
  enabled?: boolean;
}

export interface ApiError {
  code?: number;
  shortDescription?: string;
  description?: string;
}

export interface FareRequest {
  /** Customer Type Code */
  customerType: string;
  /** Zone Identifier */
  zone: string;
  /** Travel Time Identifier */
  travelTime: string;
  /** Travel Method Identifier */
  travelMethod: string;
}

export interface TicketProduct {
  id?: number;
  /** Ticket Description based on Ticket ID or Ticket Code (Description might differ when Ticket fetched based on ID or Code) */
  description?: string;
  /** Ticket Period Name (This might be different than Ticket Description) */
  ticketPeriodName?: string;
  /** Describes this Ticket Product is a Direct Debit ( true if the Ticket Product is Direct Debit Ticket ) */
  isDirectDebit?: boolean;
  /** Number of Days for '1-week' Ticket Product */
  noOfDays?: number;
  ticketArea?: TravelArea;
  ticketTime?: TravelTime;
  ticketMode?: TravelMethod;
  passengerType?: CustomerType;
  /** Term Dates values only available when Ticket Product is Term Ticket otherwise this object will be null */
  termDates?: TermDates;
  price?: Price[];
  /** MTicket ID (TicketCode) exists if Masabi Reference Available for Ticket */
  mTicketID?: string;
  /** SwiftTicket ID (TicketCode) exists if SwiftCard is associated with Ticket */
  swiftTicketID?: string;
}

/** Term Dates values only available when Ticket Product is Term Ticket otherwise this object will be null */
export interface TermDates {
  /** ISO 8601 Date Format */
  termDateFrom?: string;
  /** ISO 8601 Date Format */
  termDateTo?: string;
}

export interface Price {
  /** ISO 8601 Date Format */
  dateFrom?: string;
  /** ISO 8601 Date Format */
  dateTo?: string;
  price?: number;
  pricePerDay?: number;
  /** Fulfillment Method(s) possible values are {mTicket, Smartcard} */
  fulfillmentMethod?: string[];
  paymentMethod?: PricePaymentMethod;
}

export interface FareRequestByBundle {
  /** Ticket Selector Bundles respectively {Flexible Tickets, Pay As You Go} */
  bundleName: FareRequestByBundleName;
}

/** Known values of {@link HostOptions} that the service accepts. */
export enum KnownHostOptions {
  /** host: https://nxbus.api.nationalexpress.com */
  HttpsNxbusApiNationalexpressCom = "https://nxbus.api.nationalexpress.com",
  /** host: https://perfnxbus.api.nationalexpress.com */
  HttpsPerfnxbusApiNationalexpressCom = "https://perfnxbus.api.nationalexpress.com",
  /** host: https://dt8nxbus.api.nationalexpress.com */
  HttpsDt8NxbusApiNationalexpressCom = "https://dt8nxbus.api.nationalexpress.com",
  /** host: https://dt6nxbus.api.nationalexpress.com */
  HttpsDt6NxbusApiNationalexpressCom = "https://dt6nxbus.api.nationalexpress.com"
}

/**
 * Defines values for HostOptions. \
 * {@link KnownHostOptions} can be used interchangeably with HostOptions,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **https:\/\/nxbus.api.nationalexpress.com**: host: https:\/\/nxbus.api.nationalexpress.com \
 * **https:\/\/perfnxbus.api.nationalexpress.com**: host: https:\/\/perfnxbus.api.nationalexpress.com \
 * **https:\/\/dt8nxbus.api.nationalexpress.com**: host: https:\/\/dt8nxbus.api.nationalexpress.com \
 * **https:\/\/dt6nxbus.api.nationalexpress.com**: host: https:\/\/dt6nxbus.api.nationalexpress.com
 */
export type HostOptions = string;

/** Known values of {@link PricePaymentMethod} that the service accepts. */
export enum KnownPricePaymentMethod {
  /** OneOff */
  OneOff = "One Off",
  /** Subscription */
  Subscription = "Subscription"
}

/**
 * Defines values for PricePaymentMethod. \
 * {@link KnownPricePaymentMethod} can be used interchangeably with PricePaymentMethod,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **One Off** \
 * **Subscription**
 */
export type PricePaymentMethod = string;

/** Known values of {@link FareRequestByBundleName} that the service accepts. */
export enum KnownFareRequestByBundleName {
  /** Flexible */
  Flexible = "Flexible",
  /** PayAsYouGo */
  PayAsYouGo = "PayAsYouGo"
}

/**
 * Defines values for FareRequestByBundleName. \
 * {@link KnownFareRequestByBundleName} can be used interchangeably with FareRequestByBundleName,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Flexible** \
 * **PayAsYouGo**
 */
export type FareRequestByBundleName = string;

/** Optional parameters. */
export interface GetFareSelectorListOptionalParams
  extends coreClient.OperationOptions {
  /** Fare Selector Request */
  body?: FareSelectorRequest;
}

/** Contains response data for the getFareSelectorList operation. */
export type GetFareSelectorListResponse = FareSelectorList;

/** Optional parameters. */
export interface GetFareOptionalParams extends coreClient.OperationOptions {
  /** Fare Request */
  body?: FareRequest;
}

/** Contains response data for the getFare operation. */
export type GetFareResponse = TicketProduct[];

/** Optional parameters. */
export interface GetFareByQueryStringTicketCodeOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFareByQueryStringTicketCode operation. */
export type GetFareByQueryStringTicketCodeResponse = TicketProduct;

/** Optional parameters. */
export interface GetFareByBundleOptionalParams
  extends coreClient.OperationOptions {
  /** Fare Request BY Bundle */
  body?: FareRequestByBundle;
}

/** Contains response data for the getFareByBundle operation. */
export type GetFareByBundleResponse = TicketProduct[];

/** Optional parameters. */
export interface GetFareByTicketIDOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFareByTicketID operation. */
export type GetFareByTicketIDResponse = TicketProduct;

/** Optional parameters. */
export interface TicketFaresAPIOptionalParams
  extends coreClient.ServiceClientOptions {
  /** server parameter */
  $host?: HostOptions;
  /** Overrides client endpoint. */
  endpoint?: string;
}
